import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { AuthClientApi } from "../../../utils/api";
import NavbarMenu from "../../../Components/SharesNavbarMenu";
import { FaCheck, FaTimes } from "react-icons/fa";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { Tooltip, makeStyles } from "@material-ui/core";
import "./Fund.css";
import { toast } from "react-toastify";
import { Container } from "react-bootstrap";

function formatNumber(num) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

const useStyles = makeStyles((theme) => ({
  customTooltip: {
    backgroundColor: "rgba(0, 0, 0, 0.70)",
    color: "#ffffff",
    fontSize: "14px",
    padding: "6px 9px",
  },
  customArrow: {
    color: "rgba(0, 0, 0, 0.70)",
  },
}));

export default function Fund() {
  const classes = useStyles();
  const history = useHistory();

  // Groups State
  const [groups, setGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(true);
  const [groupsError, setGroupsError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalGroups, setTotalGroups] = useState(0);
  const [groupsPerPage] = useState(10);

  // State for each group's data
  const [groupStates, setGroupStates] = useState({});

  // Timers for countdown
  const [timers, setTimers] = useState({});

  // Refs for scroll detection
  const tableRefs = useRef({});

  // Fetch Groups Metadata
  const fetchGroups = async (page = 1) => {
    setGroupsLoading(true);
    setGroupsError(null);
    try {
      const response = await AuthClientApi.get(
        `/client/blockshares/fetch-batch-groups-funds?page=${page}&limit=${groupsPerPage}`
      );
      const data = response.data;

      if (data.status && data.data && data.data.batches) {
        setGroups(data.data.batches);
        setTotalPages(data.data.pagination.totalPages);
        setTotalGroups(data.data.pagination.totalGroups);
        setCurrentPage(data.data.pagination.page);

        // Initialize group states for new groups
        const newGroupStates = {};
        data.data.batches.forEach((group) => {
          if (!groupStates[group.approval_batch_id]) {
            newGroupStates[group.approval_batch_id] = {
              isExpanded: false,
              activeView: "pending",
              pendingFunds: [],
              pendingCompanies: [],
              pendingCompanyTotals: [],
              pendingPage: 1,
              pendingTotalPages: 1,
              pendingTotal: 0,
              pendingLoading: false,
              pendingToFundLoading: false,
              moveToTransactionLoading: false,
              expiredFunds: [],
              expiredCompanies: [],
              expiredCompanyTotals: [],
              expiredPage: 1,
              expiredTotalPages: 1,
              expiredTotal: 0,
              expiredLoading: false,
            };
          }
        });

        if (Object.keys(newGroupStates).length > 0) {
          setGroupStates((prev) => ({ ...prev, ...newGroupStates }));
        }
      } else {
        throw new Error("Invalid API response structure 1");
      }
    } catch (err) {
      setGroupsError(err.message);
      toast.error(err.message || "Failed to load groups");
    } finally {
      setGroupsLoading(false);
    }
  };

  // Fetch Pending Funds for a specific group
  const fetchGroupPendingFunds = async (batchId, page = 1, append = false) => {
    setGroupStates((prev) => ({
      ...prev,
      [batchId]: {
        ...prev[batchId],
        pendingLoading: true,
      },
    }));

    try {
      const response = await AuthClientApi.get(
        `/client/blockshares/fetch-pending-funds?batchId=${batchId}&page=${page}&limit=10`
      );
      const data = response.data;

      // Extract companies from company_totals (handle empty gracefully)
      const companiesFromAPI = data.data?.company_totals
        ? data.data.company_totals.map((ct) => ct.short_name)
        : [];

      // Transform funds data (empty array is valid)
      const transformedFunds =
        data.data?.funds && data.data.funds.length > 0
          ? data.data.funds.map((fund) => {
              const amounts = {};
              const companyStatuses = {};
              companiesFromAPI.forEach((company) => {
                amounts[company] = 0;
                companyStatuses[company] = null;
              });

              if (fund.plan && fund.plan.plan_companies) {
                fund.plan.plan_companies.forEach((pc) => {
                  const companyName = pc.Company.short_name;
                  amounts[companyName] = parseFloat(pc.allocated_amount);
                  companyStatuses[companyName] = pc.status;
                });
              }

              return {
                id: fund.id,
                planId: fund.plan.id,
                tradingNumber: fund.plan.customer?.trading_number || "N/A",
                date: new Date(fund.plan.submitted_at).toLocaleDateString(
                  "en-GB"
                ),
                amounts: amounts,
                companyStatuses: companyStatuses,
                status: fund.status,
                countdown: fund.countdown,
                customer: fund.plan.customer,
                totalAmount: fund.plan.total_amount,
                plan: fund.plan,
                approvalBatchId: fund.plan.approval_batch_id,
                approvalType: fund.plan.approval_type,
              };
            })
          : [];

      // Initialize countdown timers
      const newTimers = {};
      if (data.data?.funds && data.data.funds.length > 0) {
        data.data.funds.forEach((fund) => {
          if (fund.countdown && fund.countdown.remaining_ms > 0) {
            newTimers[fund.id] = {
              expiresAt: fund.countdown.expires_at,
              remainingMs: fund.countdown.remaining_ms,
            };
          }
        });
      }
      setTimers((prev) => ({ ...prev, ...newTimers }));

      setGroupStates((prev) => {
        // If appending, accumulate company totals from previous state
        let updatedCompanyTotals = data.data?.company_totals || [];

        if (append && prev[batchId]?.pendingCompanyTotals) {
          // Create a map of existing totals
          const existingTotalsMap = {};
          prev[batchId].pendingCompanyTotals.forEach((ct) => {
            existingTotalsMap[ct.short_name] = ct.total_allocated_amount;
          });

          // Accumulate new totals with existing ones
          updatedCompanyTotals = (data.data?.company_totals || []).map(
            (ct) => ({
              ...ct,
              total_allocated_amount:
                (existingTotalsMap[ct.short_name] || 0) +
                ct.total_allocated_amount,
            })
          );
        }

        return {
          ...prev,
          [batchId]: {
            ...prev[batchId],
            pendingFunds: append
              ? [...prev[batchId].pendingFunds, ...transformedFunds]
              : transformedFunds,
            pendingCompanies: companiesFromAPI,
            pendingCompanyTotals: updatedCompanyTotals,
            pendingPage: data.data?.pagination?.page || page,
            pendingTotalPages: data.data?.pagination?.totalPages || 1,
            pendingTotal: data.data?.pagination?.total || 0,
            pendingLoading: false,
          },
        };
      });
    } catch (err) {
      toast.error(err.message || "Failed to load pending funds");
      setGroupStates((prev) => ({
        ...prev,
        [batchId]: {
          ...prev[batchId],
          pendingLoading: false,
        },
      }));
    }
  };

  // Fetch Expired/Cancelled Funds for a specific group
  const fetchGroupExpiredFunds = async (batchId, page = 1, append = false) => {
    setGroupStates((prev) => ({
      ...prev,
      [batchId]: {
        ...prev[batchId],
        expiredLoading: true,
      },
    }));

    try {
      const response = await AuthClientApi.get(
        `/client/blockshares/fetch-expired-cancelled-funds?batchId=${batchId}&page=${page}&limit=10`
      );
      const data = response.data;

      // Extract companies (handle empty gracefully)
      const companiesFromAPI = data.data?.company_totals
        ? data.data.company_totals.map((ct) => ct.short_name)
        : [];

      // Transform funds data (empty array is valid)
      const transformedFunds =
        data.data?.funds && data.data.funds.length > 0
          ? data.data.funds.map((fund) => {
              const amounts = {};
              const companyStatuses = {};
              companiesFromAPI.forEach((company) => {
                amounts[company] = 0;
                companyStatuses[company] = null;
              });

              if (fund.plan && fund.plan.plan_companies) {
                fund.plan.plan_companies.forEach((pc) => {
                  const companyName = pc.Company.short_name;
                  amounts[companyName] = parseFloat(pc.allocated_amount);
                  companyStatuses[companyName] = pc.status;
                });
              }

              return {
                id: fund.id,
                planId: fund.plan.id,
                tradingNumber: fund.plan.customer?.trading_number || "N/A",
                date: new Date(fund.plan.submitted_at).toLocaleDateString(
                  "en-GB"
                ),
                amounts: amounts,
                companyStatuses: companyStatuses,
                status: fund.status,
                customer: fund.plan.customer,
                totalAmount: fund.plan.total_amount,
                expiredAt: fund.expired_at,
                plan: fund.plan,
                approvalBatchId: fund.plan.approval_batch_id,
                approvalType: fund.plan.approval_type,
              };
            })
          : [];

      setGroupStates((prev) => {
        // If appending, accumulate company totals from previous state
        let updatedCompanyTotals = data.data?.company_totals || [];

        if (append && prev[batchId]?.expiredCompanyTotals) {
          // Create a map of existing totals
          const existingTotalsMap = {};
          prev[batchId].expiredCompanyTotals.forEach((ct) => {
            existingTotalsMap[ct.short_name] = ct.total_allocated_amount;
          });

          // Accumulate new totals with existing ones
          updatedCompanyTotals = (data.data?.company_totals || []).map(
            (ct) => ({
              ...ct,
              total_allocated_amount:
                (existingTotalsMap[ct.short_name] || 0) +
                ct.total_allocated_amount,
            })
          );
        }

        return {
          ...prev,
          [batchId]: {
            ...prev[batchId],
            expiredFunds: append
              ? [...prev[batchId].expiredFunds, ...transformedFunds]
              : transformedFunds,
            expiredCompanies: companiesFromAPI,
            expiredCompanyTotals: updatedCompanyTotals,
            expiredPage: data.data?.pagination?.page || page,
            expiredTotalPages: data.data?.pagination?.totalPages || 1,
            expiredTotal: data.data?.pagination?.total || 0,
            expiredLoading: false,
          },
        };
      });
    } catch (err) {
      toast.error(err.message || "Failed to load expired funds");
      setGroupStates((prev) => ({
        ...prev,
        [batchId]: {
          ...prev[batchId],
          expiredLoading: false,
        },
      }));
    }
  };

  // Countdown timer effect
  useEffect(() => {
    const timer = setInterval(() => {
      setTimers((prevTimers) => {
        const updatedTimers = {};
        Object.keys(prevTimers).forEach((fundId) => {
          const timer = prevTimers[fundId];
          const expiresAt = new Date(timer.expiresAt);
          const now = new Date();
          const remainingMs = expiresAt.getTime() - now.getTime();

          if (remainingMs > 0) {
            updatedTimers[fundId] = {
              expiresAt: timer.expiresAt,
              remainingMs: remainingMs,
            };
          } else {
            // Timer expired - update fund status in all groups
            setGroupStates((prevGroupStates) => {
              const updatedGroupStates = { ...prevGroupStates };
              Object.keys(updatedGroupStates).forEach((batchId) => {
                if (updatedGroupStates[batchId].pendingFunds.length > 0) {
                  updatedGroupStates[batchId].pendingFunds = updatedGroupStates[
                    batchId
                  ].pendingFunds.map((fund) =>
                    fund.id === parseInt(fundId)
                      ? { ...fund, status: "Expired" }
                      : fund
                  );
                }
              });
              return updatedGroupStates;
            });
          }
        });
        return updatedTimers;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchGroups(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatCountdownTimer = (fundId) => {
    const timer = timers[fundId];
    if (!timer || timer.remainingMs <= 0) {
      return "Expired";
    }

    const totalSeconds = Math.floor(timer.remainingMs / 1000);
    const days = Math.floor(totalSeconds / (24 * 3600));
    const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${days}d:${String(hours).padStart(2, "0")}h:${String(
      minutes
    ).padStart(2, "0")}m:${String(seconds).padStart(2, "0")}s`;
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      fetchGroups(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      fetchGroups(currentPage + 1);
    }
  };

  const toggleExpand = (batchId) => {
    const isCurrentlyExpanded = groupStates[batchId]?.isExpanded;

    setGroupStates((prev) => ({
      ...prev,
      [batchId]: {
        ...prev[batchId],
        isExpanded: !isCurrentlyExpanded,
      },
    }));

    // Fetch funds on first expand
    if (
      !isCurrentlyExpanded &&
      groupStates[batchId]?.pendingFunds.length === 0
    ) {
      fetchGroupPendingFunds(batchId, 1);
      fetchGroupExpiredFunds(batchId, 1);
    }
  };

  const switchToPendingView = (batchId) => {
    setGroupStates((prev) => ({
      ...prev,
      [batchId]: {
        ...prev[batchId],
        activeView: "pending",
      },
    }));
  };

  const switchToExpiredView = (batchId) => {
    setGroupStates((prev) => ({
      ...prev,
      [batchId]: {
        ...prev[batchId],
        activeView: "expired",
      },
    }));
  };

  const handleTableScroll = (e, batchId, activeView) => {
    const element = e.target;
    // Add a small threshold (5px) to account for sub-pixel rendering
    const scrollThreshold = 5;
    const bottom =
      element.scrollHeight - element.scrollTop <=
      element.clientHeight + scrollThreshold;

    if (bottom) {
      const groupState = groupStates[batchId];
      if (!groupState) return;

      if (activeView === "pending") {
        if (
          groupState.pendingPage < groupState.pendingTotalPages &&
          !groupState.pendingLoading
        ) {
          fetchGroupPendingFunds(batchId, groupState.pendingPage + 1, true);
        }
      } else {
        if (
          groupState.expiredPage < groupState.expiredTotalPages &&
          !groupState.expiredLoading
        ) {
          fetchGroupExpiredFunds(batchId, groupState.expiredPage + 1, true);
        }
      }
    }
  };

  const handlePendingToFund = async (batchId) => {
    setGroupStates((prev) => ({
      ...prev,
      [batchId]: {
        ...prev[batchId],
        pendingToFundLoading: true,
      },
    }));

    try {
      const response = await AuthClientApi.put(
        "/client/blockshares/initiate-fund-timer",
        { batch_id: batchId }
      );
      if (response.data.status) {
        toast.success(
          response.data.message || "Fund timer initiated successfully"
        );
        // Refresh pending funds to get updated countdown timers
        const groupState = groupStates[batchId];
        if (groupState) {
          await fetchGroupPendingFunds(batchId, groupState.pendingPage);
        }
      } else {
        throw new Error(
          response.data.message || "Failed to initiate fund timer"
        );
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Failed to initiate fund timer"
      );
    } finally {
      setGroupStates((prev) => ({
        ...prev,
        [batchId]: {
          ...prev[batchId],
          pendingToFundLoading: false,
        },
      }));
    }
  };

  const handleMoveToTransaction = async (batchId) => {
    setGroupStates((prev) => ({
      ...prev,
      [batchId]: {
        ...prev[batchId],
        moveToTransactionLoading: true,
      },
    }));

    try {
      const response = await AuthClientApi.post(
        "/client/blockshares/funds/move-to-transaction",
        { batch_id: batchId }
      );

      if (response.data.count === 0) {
        throw new Error("No funds available to move to transaction");
      } else if (response.data.status && response.data.count > 0) {
        toast.success(
          response.data.message || "Successfully moved to transaction"
        );

        history.push("/shares-platform/transaction");
      } else {
        throw new Error(
          response.data.message || "Failed to move to transaction"
        );
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.message ||
          "Failed to move to transaction"
      );
    } finally {
      setGroupStates((prev) => ({
        ...prev,
        [batchId]: {
          ...prev[batchId],
          moveToTransactionLoading: false,
        },
      }));
    }
  };

  const handleRefundPlan = async (batchId) => {
    // TODO: Implement API call
    toast.info(`Refunding group ${batchId}`);
  };

  const handleRefundCompany = async (company, batchId, isExpired = false) => {
    // TODO: Implement API call
    toast.info(`Refunding ${company} for group ${batchId}`);
  };

  const getTotalForCompany = (company, companyTotals) => {
    const companyTotal = companyTotals.find((ct) => ct.short_name === company);
    return companyTotal ? companyTotal.total_allocated_amount : 0;
  };

  const getGrandTotal = (companyTotals) => {
    return companyTotals.reduce(
      (grandTotal, ct) => grandTotal + ct.total_allocated_amount,
      0
    );
  };

  const handleBackToDashboard = () => {
    history.push("/shares-platform");
  };

  return (
    <>
      <NavbarMenu />

      <div className="fund-wrapper">
        <button
          onClick={handleBackToDashboard}
          className="back-to-dashboard-btn"
          title="Back to Dashboard"
        >
          <ArrowBackIcon />
        </button>
        <h1 className="fund-title">Fund</h1>

        <Container fluid className="fund-container fade-in">
          {/* Loading State */}
          {groupsLoading && (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "200px" }}
            >
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {groupsError && (
            <div className="alert alert-danger" role="alert">
              Error loading groups: {groupsError}
              <button
                className="btn btn-outline-danger btn-sm ms-2"
                onClick={() => fetchGroups(currentPage)}
              >
                Retry
              </button>
            </div>
          )}

          {/* No Groups */}
          {!groupsLoading && !groupsError && groups.length === 0 && (
            <div
              className="alert alert-info"
              role="alert"
              style={{ textAlign: "center", padding: "20px" }}
            >
              No groups available.
            </div>
          )}

          {/* Render Groups */}
          {!groupsLoading &&
            !groupsError &&
            groups.length > 0 &&
            groups.map((group, index) => {
              const groupState = groupStates[group.approval_batch_id] || {
                isExpanded: false,
                activeView: "pending",
                pendingFunds: [],
                pendingCompanies: [],
                pendingLoading: false,
                pendingToFundLoading: false,
                moveToTransactionLoading: false,
                expiredFunds: [],
                expiredCompanies: [],
                expiredLoading: false,
              };

              const isExpanded = groupState.isExpanded;
              const activeView = groupState.activeView;
              const groupNumber = (currentPage - 1) * groupsPerPage + index + 1;

              // Get current view data
              const currentFunds =
                activeView === "pending"
                  ? groupState.pendingFunds
                  : groupState.expiredFunds;
              const currentCompanies =
                activeView === "pending"
                  ? groupState.pendingCompanies
                  : groupState.expiredCompanies;
              const currentCompanyTotals =
                activeView === "pending"
                  ? groupState.pendingCompanyTotals
                  : groupState.expiredCompanyTotals;
              const currentLoading =
                activeView === "pending"
                  ? groupState.pendingLoading
                  : groupState.expiredLoading;
              const allFundsCompleted = currentFunds.every(
                (fund) => fund.plan.status === "Completed"
              );

              return (
                <div key={group.approval_batch_id} className="fund-batch-card">
                  <div className="fund-batch-header">
                    <div className="fund-batch-info">
                      <span className="fund-batch-title">
                        Group {groupNumber}
                        {/* - Batch ID:{" "}
                        {group.approval_batch_id} */}
                      </span>
                      {isExpanded && (
                        <span
                          style={{
                            marginLeft: "15px",
                            color: "#666",
                            fontSize: "14px",
                          }}
                        >
                          Total Funds: {currentFunds.length}
                          {/* Total Funds: {group.total_plans} */}
                        </span>
                      )}
                    </div>
                    <div className="fund-batch-buttons">
                      <button
                        className="btn-fund-expand"
                        onClick={() => toggleExpand(group.approval_batch_id)}
                      >
                        {isExpanded ? "Collapse" : "Expand"}
                      </button>
                      {isExpanded && activeView === "pending" && (
                        <button
                          className="btn-fund-toggle"
                          onClick={() =>
                            handlePendingToFund(group.approval_batch_id)
                          }
                          disabled={
                            groupState.pendingToFundLoading ||
                            currentFunds.length === 0
                          }
                        >
                          {groupState.pendingToFundLoading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            </>
                          ) : (
                            "Pending to fund"
                          )}
                        </button>
                      )}
                      {isExpanded && activeView === "expired" && (
                        <button
                          className="btn-fund-toggle"
                          onClick={() =>
                            switchToPendingView(group.approval_batch_id)
                          }
                          style={{
                            backgroundColor: "#007bff",
                            borderColor: "#007bff",
                          }}
                        >
                          View Pending Funds
                        </button>
                      )}
                      {isExpanded && (
                        <>
                          <button
                            className="btn-fund-refund"
                            onClick={() =>
                              handleRefundPlan(group.approval_batch_id)
                            }
                            disabled={
                              activeView === "pending" ||
                              currentFunds.length === 0
                            }
                          >
                            Refund Plan
                          </button>
                          <button
                            className="btn-fund-approve"
                            onClick={() =>
                              handleMoveToTransaction(group.approval_batch_id)
                            }
                            disabled={
                              activeView === "expired" ||
                              allFundsCompleted ||
                              currentFunds.length === 0 ||
                              groupState.moveToTransactionLoading
                            }
                            style={{
                              display:
                                activeView === "pending"
                                  ? "inline-flex"
                                  : "none",
                            }}
                          >
                            {groupState.moveToTransactionLoading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Moving...
                              </>
                            ) : (
                              <>
                                <FaCheck /> Move to transaction
                              </>
                            )}
                          </button>
                          <button
                            className={`status-display ${
                              activeView === "expired" ? "active" : ""
                            }`}
                            onClick={() =>
                              switchToExpiredView(group.approval_batch_id)
                            }
                            style={{
                              backgroundColor:
                                activeView === "expired"
                                  ? "#dc3545"
                                  : "#6c757d",
                              borderColor:
                                activeView === "expired"
                                  ? "#dc3545"
                                  : "#6c757d",
                              cursor: "pointer",
                            }}
                          >
                            <FaTimes /> Canceled/Expired
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {isExpanded && (
                    <>
                      <div
                        className="custom-scroll"
                        style={{ maxHeight: "400px", overflowY: "auto" }}
                        onScroll={(e) =>
                          handleTableScroll(
                            e,
                            group.approval_batch_id,
                            activeView
                          )
                        }
                        ref={(el) =>
                          (tableRefs.current[group.approval_batch_id] = el)
                        }
                      >
                        <table className="table table-bordered fund-table">
                          <thead>
                            <tr className="fund-header-row">
                              <th className="sticky-col">Trading #</th>
                              <th className="sticky-col-2">Date</th>
                              <th className="sticky-col-3">Time</th>
                              {currentCompanies.map((company) => (
                                <th key={company}>
                                  <div className="company-col">
                                    {company}
                                    <button
                                      className="btn-refund-bal"
                                      onClick={() =>
                                        handleRefundCompany(
                                          company,
                                          group.approval_batch_id,
                                          activeView === "expired"
                                        )
                                      }
                                      disabled={
                                        (activeView === "pending" &&
                                          (allFundsCompleted ||
                                            currentFunds.length === 0)) ||
                                        (activeView === "expired" &&
                                          currentFunds.length === 0)
                                      }
                                    >
                                      Refund
                                    </button>
                                  </div>
                                </th>
                              ))}
                              <th className="sticky-col-right">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {currentFunds.length === 0 && !currentLoading ? (
                              <tr>
                                <td
                                  colSpan={4 + currentCompanies.length}
                                  style={{
                                    textAlign: "center",
                                    padding: "20px",
                                    color: "#666",
                                  }}
                                >
                                  {activeView === "pending"
                                    ? "No Pending Funds Available for this group."
                                    : "No Expired/Cancelled Funds Available for this group."}
                                </td>
                              </tr>
                            ) : (
                              currentFunds.map((fund, index) => (
                                <tr key={index}>
                                  <td className="sticky-col">
                                    {fund.tradingNumber}
                                  </td>
                                  <td className="sticky-col-2">{fund.date}</td>
                                  <td className="sticky-col-3">
                                    {activeView === "pending" ? (
                                      fund.countdown ? (
                                        <span
                                          style={{
                                            color: "#000",
                                            fontWeight: "600",
                                          }}
                                        >
                                          {formatCountdownTimer(fund.id)}
                                        </span>
                                      ) : (
                                        <span style={{ color: "#999" }}>
                                          {fund.plan.status === "Expired"
                                            ? "Expired"
                                            : fund.plan.status === "Canceled"
                                            ? "Canceled"
                                            : "3d:00h:00m:00s"}
                                        </span>
                                      )
                                    ) : (
                                      <span
                                        style={{
                                          color: "#dc3545",
                                          fontWeight: "600",
                                        }}
                                      >
                                        {fund.plan.status === "Expired"
                                          ? "Expired"
                                          : "Canceled"}
                                      </span>
                                    )}
                                  </td>
                                  {currentCompanies.map((company) => {
                                    const amount = fund.amounts[company] || 0;
                                    const companyStatus =
                                      fund.companyStatuses?.[company];
                                    // const isFunded = companyStatus === "Funded";
                                    return (
                                      <td key={company}>
                                        <Tooltip
                                          title={companyStatus}
                                          placement="top"
                                          arrow
                                          classes={{
                                            tooltip: classes.customTooltip,
                                            arrow: classes.customArrow,
                                          }}
                                        >
                                          <span style={{ cursor: "pointer" }}>
                                            {amount > 0
                                              ? formatNumber(amount)
                                              : 0}
                                          </span>
                                        </Tooltip>
                                      </td>
                                    );
                                  })}
                                  <td className="sticky-col-right">
                                    <span
                                      style={{
                                        color:
                                          fund.plan.status === "Pending"
                                            ? "#28a745"
                                            : fund.plan.status === "Expired"
                                            ? "#dc3545"
                                            : fund.plan.status === "Canceled"
                                            ? "#dc3545"
                                            : "#000",
                                        fontWeight: "600",
                                      }}
                                    >
                                      {fund?.plan?.status}
                                    </span>
                                  </td>
                                </tr>
                              ))
                            )}
                            {currentLoading && (
                              <tr>
                                <td
                                  colSpan={4 + currentCompanies.length}
                                  style={{
                                    textAlign: "center",
                                    padding: "20px",
                                  }}
                                >
                                  <div
                                    className="spinner-border text-primary"
                                    role="status"
                                  >
                                    <span className="sr-only">
                                      Loading more...
                                    </span>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                          {currentFunds.length > 0 && (
                            <tfoot>
                              <tr className="fund-total-row">
                                <td className="sticky-col">
                                  {currentFunds.length}
                                </td>
                                <td className="sticky-col-2">Total</td>
                                <td className="sticky-col-3"></td>
                                {currentCompanies.map((company) => {
                                  const total = getTotalForCompany(
                                    company,
                                    currentCompanyTotals
                                  );
                                  return (
                                    <td key={company}>
                                      {total > 0 ? formatNumber(total) : 0}
                                    </td>
                                  );
                                })}
                                <td className="sticky-col-right">
                                  {formatNumber(
                                    getGrandTotal(currentCompanyTotals)
                                  )}
                                </td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
        </Container>

        {/* Pagination - Previous/Next Buttons */}
        {!groupsLoading && !groupsError && groups.length > 0 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "20px 40px",
              marginTop: "20px",
            }}
          >
            {/* Left - Previous Button */}
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              style={{
                padding: "10px 20px",
                backgroundColor: currentPage === 1 ? "#3f3838" : "#007bff",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: currentPage === 1 ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "600",
                visibility: currentPage === 1 ? "hidden" : "visible",
              }}
            >
              ← Previous Page
            </button>

            {/* Center - Page Info */}
            <span
              style={{
                color: "#666",
                fontSize: "14px",
                // display: groups.length >= 10 ? "block" : "none",
              }}
            >
              Page {currentPage} of {totalPages} ({totalGroups} groups total)
            </span>

            {/* Right - Next Button */}
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              style={{
                padding: "10px 20px",
                backgroundColor:
                  currentPage === totalPages ? "#3f3838" : "#007bff",
                color: "#fff",
                border: "none",
                borderRadius: "4px",
                cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                fontSize: "14px",
                fontWeight: "600",
                visibility: currentPage === totalPages ? "hidden" : "visible",
              }}
            >
              Next Page →
            </button>
          </div>
        )}
      </div>
    </>
  );
}
